import React, { useEffect, useState, useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import { Field, ErrorMessage } from '@atlaskit/form';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import {
	PRIORITY_SCHEME_MODAL_PRIORITIES_FIELD_VALIDATION_ERROR_SELECTOR,
	PRIORITY_SCHEME_MODAL_PRIORITY_PICKER_DROPDOWN_MENU_SELECTOR,
} from '../../../../common/constants';
import PrioritiesPicker from '../../../../common/ui/priority-picker/main.tsx';
import type { Page, Priority, PriorityOption } from '../../../../common/ui/types';
import { fetchAvailablePriorities } from '../../../../services/priorities';
import PrioritiesList from './list/main.tsx';
import messages from './messages';

export type Props = {
	priorities: Priority[];
	error: string;
	onChange: (priorities: Priority[]) => void;
	/**
	 * Given a priority Id, remove all mappings that are mapped to that priority
	 * @param removedPriorityId the priority that was removed from mapping list
	 */
	onRemovePriorityMapping?: (removedPriorityId: string) => void;
};

const PICKER_PAGE_SIZE = 8;
const emptyInitialPage: Page<PriorityOption> = {
	items: [],
	currentPage: 0,
	pageSize: PICKER_PAGE_SIZE,
	totalItems: 0,
};

export default function Priorities({
	onChange,
	priorities,
	error,
	onRemovePriorityMapping,
}: Props) {
	const { formatMessage } = useIntl();

	const [isPickerOpen, setIsOpen] = useState(false);
	const [isPickerLoading, setIsPickerLoading] = useState(false);
	const [pickerFilter, setPickerFilter] = useState('');

	// okay, this is a bit of a hack, but we need to keep track of the picker items in a ref
	// so that we can use the latest version of the items in the loadMore method
	// with useEffect loadMore will allways use captured version of pickerItems and not the latest one
	const [, setPickerItemsVersion] = useState<number>(0);
	const pickerItems = useRef<Page<PriorityOption>>(emptyInitialPage);
	const setPickerItems = (items: Page<PriorityOption>) => {
		pickerItems.current = items;
		// we need to update the version to force a re-render
		setPickerItemsVersion((v) => v + 1);
	};

	const selectedIds: Record<string, true> = useMemo(() => {
		const ids: Record<string, true> = {};
		priorities.forEach((p) => {
			ids[p.id] = true;
		});
		return ids;
	}, [priorities]);

	useEffect(() => {
		// here we remove the selected priorities from the picker items
		// so that they don't appear in the picker
		const currentItems = pickerItems.current.items;
		const filteredItems = currentItems.filter((p) => !selectedIds[p.id]);

		if (filteredItems.length === currentItems.length) {
			// no changes, so we don't need to update the picker items
			// otherwise we can end up with an infinite loop of useEffects
			return;
		}

		setPickerItems({
			...pickerItems.current,
			pageSize: PICKER_PAGE_SIZE,
			// we should reduce the totalItems by the number of selected priorities that were removed from the picker items
			// we are also using Math.max here to avoid some weird edge cases and UI with negative numbers
			totalItems: Math.max(
				pickerItems.current.totalItems - (currentItems.length - filteredItems.length),
				0,
			),
			items: filteredItems,
		});
	}, [selectedIds, pickerItems]);

	const onRemove = (priority: Priority) => {
		if (onRemovePriorityMapping) {
			onRemovePriorityMapping(priority.id);
		}

		const newPriorities = priorities.filter((p) => p.id !== priority.id);
		if (priority.isDefault && newPriorities.length) {
			// there should always be a default priority, so if the previous default was removed we make the first one default
			newPriorities[0] = { ...newPriorities[0], isDefault: true };
		}
		onChange(newPriorities);
	};

	const onSetDefault = (priority: Priority) => {
		onChange(priorities.map((p) => ({ ...p, isDefault: p.id === priority.id })));
	};

	const fetch = async (filter: string) => {
		try {
			const page = await fetchAvailablePriorities(
				filter,
				0,
				PICKER_PAGE_SIZE,
				Object.keys(selectedIds),
			);
			if (filter !== pickerFilter) {
				// if the filter has changed since we started fetching, we don't want to update the items
				return;
			}
			setPickerItems(page);
		} catch (e) {
			// at this point we don't want to do anything, we just want to stop the loading indicator
			// in the future we might want to show an error message
		}
		setIsPickerLoading(false);
	};
	const [debouncedFetch] = useDebouncedCallback(fetch, 300);

	const onLoadMore = async (filter: string, exclude: string[]) => {
		setIsPickerLoading(true);
		try {
			const page = await fetchAvailablePriorities(
				filter,
				pickerItems.current.currentPage + 1,
				PICKER_PAGE_SIZE,
				exclude,
				Object.keys(selectedIds),
			);
			if (filter !== pickerFilter) {
				// if the filter has changed since we started fetching, we don't want to update the items
				return;
			}

			// for different reasons, the API can return priorities that are already in the picker items
			// so we need to filter them out, otherwise we will have duplicates in the picker
			const alreadyLoadedItems: Record<string, true> = {};
			pickerItems.current.items.forEach((p) => {
				alreadyLoadedItems[p.id] = true;
			});

			setPickerItems({
				...page,
				items: [
					...pickerItems.current.items,
					...page.items.filter((p) => !alreadyLoadedItems[p.id]),
				],
			});
		} catch (e) {
			// at this point we don't want to do anything, we just want to stop the loading indicator
			// in the future we might want to show an error message
		}
		setIsPickerLoading(false);
	};

	const onSelect = ({ id, iconUrl, name, sequence }: PriorityOption) => {
		if (selectedIds[id]) {
			return;
		}

		const newPriorities = [
			...priorities,
			// if it's the first priority in the list, make it the default
			{ id, iconUrl, name, isDefault: priorities.length === 0, sequence },
		];
		newPriorities.sort((a, b) => a.sequence - b.sequence);

		if (
			pickerItems.current.items.length <= 2 &&
			!isPickerLoading &&
			pickerItems.current.totalItems > PICKER_PAGE_SIZE
		) {
			// user is running out of available priorities,
			// and there are more available priorities to load
			// so lets load the next page in advance
			onLoadMore(
				pickerFilter,
				newPriorities.map((p) => p.id),
			);
		}

		onChange(newPriorities);
	};

	const onPickerOpen = () => {
		setIsOpen(true);
		// we should always reset the available priorities when the picker is opened
		// otherwise we can miss some priorites that have been removed
		setPickerItems(emptyInitialPage);
		setPickerFilter('');
		setIsPickerLoading(true);
		// we are using fetch here instead of debouncedFetch because we want to fetch the first page immediately
		fetch('');
	};

	const onPickerClose = () => {
		setIsOpen(false);
		setPickerItems(emptyInitialPage);
		setPickerFilter('');
	};

	const onFilterChange = (value: string) => {
		setPickerFilter(value);
		setIsPickerLoading(true);
		// we should always reset the available priorities when the filter changes
		setPickerItems(emptyInitialPage);
		// any filter change should be debounced to avoid unnecessary API calls
		debouncedFetch(value);
	};

	return (
		<Field label={formatMessage(messages.prioritiesLabel)} name="priorities" isRequired>
			{() => (
				<>
					{error ? (
						<ErrorMessage testId={PRIORITY_SCHEME_MODAL_PRIORITIES_FIELD_VALIDATION_ERROR_SELECTOR}>
							<div aria-live="polite">{error}</div>
						</ErrorMessage>
					) : null}

					<PrioritiesList onRemove={onRemove} onSetDefault={onSetDefault} priorities={priorities} />
					<PickerWrapper>
						<PrioritiesPicker
							showCheckboxes={false}
							selectedPriorityIds={null}
							renderTrigger={formatMessage(messages.addPrioritiesToggle)}
							isLoading={isPickerLoading}
							filter={pickerFilter}
							onSelect={onSelect}
							onLoadMore={() => onLoadMore(pickerFilter, Object.keys(selectedIds))}
							onFilterChange={onFilterChange}
							page={pickerItems.current}
							onOpen={onPickerOpen}
							onClose={onPickerClose}
							isOpen={isPickerOpen}
							testId={PRIORITY_SCHEME_MODAL_PRIORITY_PICKER_DROPDOWN_MENU_SELECTOR}
						/>
					</PickerWrapper>
				</>
			)}
		</Field>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PickerWrapper = styled.div({
	marginTop: `${token('space.100', '8px')}`,
});
